import type { GameStateManager } from '../game/GameState';
import type { GameData, RealityModifier, RealityModifierEffect, ActiveRealityEffect, LocationId } from '../game/types';

export class RealityBreachSystem {
  constructor(
    private gameState: GameStateManager,
    private gameData: GameData
  ) {}

  checkForRealityBreach(): void {
    const state = this.gameState.getState();
    
    // Only check if anchoring is negative
    if (state.anchoring >= 0) {
      return;
    }

    const negativeAmount = Math.abs(state.anchoring);
    
    // Roll 1% chance for each negative point
    for (let i = 0; i < negativeAmount; i++) {
      const roll = Math.random();
      if (roll < 0.01) { // 1% chance
        this.triggerRealityBreach();
        return; // Only one breach per tick
      }
    }
  }

  private triggerRealityBreach(): void {
    // Check if there's already a pending reality breach - prevent flooding
    if (this.gameState.getPendingRealityBreach() !== null) {
      return;
    }

    const modifier = this.gameState.drawRealityModifier();
    
    if (!modifier) {
      return; // No modifiers available
    }

    // Validate and provide defaults for missing data
    const modifierName = modifier.name?.trim() || 'Unknown Breach';
    const modifierDescription = modifier.description?.trim() || 'The nature of this reality breach is unknown.';
    const effects = modifier.effects || [];

    // Queue pending reality breach result
    this.gameState.queueRealityBreach({
      modifierName,
      modifierDescription,
      effects
    });
  }

  applyRealityModifier(modifier: { name: string; description: string; effects: RealityModifierEffect[] }): void {
    // First, increase anchoring by +4 (reality attempts to heal itself)
    this.gameState.modifyAnchoring(4);

    // Validate and provide defaults
    const modifierName = modifier.name?.trim() || 'Unknown Breach';
    const modifierDescription = modifier.description?.trim() || 'The nature of this reality breach is unknown.';
    const effects = modifier.effects || [];

    // Apply each effect
    for (const effect of effects) {
      this.applyEffect(effect, modifierName, modifierDescription);
    }
  }

  private applyEffect(effect: RealityModifierEffect, modifierName: string, modifierDescription: string): void {
    const state = this.gameState.getState();

    // Immediate effects
    if (effect.budgetLoss !== undefined) {
      this.gameState.addBudget(-effect.budgetLoss);
    }

    if (effect.allLoseMaxSanity !== undefined) {
      this.applyMaxSanityLoss(effect.allLoseMaxSanity);
    }

    if (effect.loseChemCapacity !== undefined) {
      this.applyChemCapacityLoss(effect.loseChemCapacity);
    }

    if (effect.cellDisappears !== undefined) {
      this.applyCellDisappearance(effect.cellDisappears);
    }

    if (effect.killRandomResearcher !== undefined && effect.killRandomResearcher) {
      this.applyKillRandomResearcher();
    }

    // Persistent effects - add to active effects
    if (effect.recruitmentForbidden !== undefined && effect.recruitmentForbidden) {
      this.addPersistentEffect({
        name: modifierName,
        description: modifierDescription,
        effect: { recruitmentForbidden: true }
      });
    }

    if (effect.movementTimeMultiplier !== undefined) {
      this.addPersistentEffect({
        name: modifierName,
        description: modifierDescription,
        effect: { movementTimeMultiplier: effect.movementTimeMultiplier }
      });
    }

    if (effect.extraBudjetLossPerTick !== undefined) {
      this.addPersistentEffect({
        name: modifierName,
        description: modifierDescription,
        effect: { extraBudjetLossPerTick: effect.extraBudjetLossPerTick }
      });
    }
  }

  private applyMaxSanityLoss(amount: number): void {
    const researchers = this.gameState.getResearchers();
    
    for (const researcher of researchers) {
      // Reduce max sanity
      researcher.data.sanity = Math.max(1, researcher.data.sanity - amount);
      
      // If current sanity exceeds new max, reduce it
      if (researcher.currentSanity > researcher.data.sanity) {
        researcher.currentSanity = researcher.data.sanity;
      }
    }
  }

  private applyChemCapacityLoss(newCapacity: number): void {
    const state = this.gameState.getState();
    
    // Recalculate max chemicals considering Expert_Chemist bonuses
    const baseMax = newCapacity;
    const expertChemistCount = state.researchers.filter(r => 
      r.data.special_rules.includes('Expert_Chemist')
    ).length;
    state.maxChemicals = baseMax + expertChemistCount;
    
    // Cap current chemicals to new maximum
    if (state.chemicals > state.maxChemicals) {
      state.chemicals = state.maxChemicals;
    }
  }

  private applyCellDisappearance(cellNumber: number): void {
    if (cellNumber !== 4) {
      return; // Only cell 4 can disappear based on user requirements
    }

    this.gameState.setCell4Disappeared(true);
    const cellId: LocationId = 'cell_4';
    
    // Evacuate researchers from cell_4 and corridors
    const researchers = this.gameState.getResearchers();
    const breachedAnomalies = this.gameState.getBreachedAnomalies();
    
    for (const researcher of researchers) {
      // Check if in cell_4
      if (researcher.location === cellId) {
        researcher.location = 'control_room';
        researcher.targetLocation = null;
        researcher.movementProgress = 0;
      }
      
      // Check if moving to/from cell_4
      if (researcher.targetLocation === cellId) {
        researcher.location = 'control_room';
        researcher.targetLocation = null;
        researcher.movementProgress = 0;
      }
      
      // Check if currently in a corridor connected to cell_4
      if (researcher.location === 'cell_3' && researcher.targetLocation === cellId) {
        researcher.location = 'control_room';
        researcher.targetLocation = null;
        researcher.movementProgress = 0;
      }
    }
    
    // Handle breached anomalies in cell_4
    const breachedInCell4 = breachedAnomalies.filter(b => b.breachCellId === cellId);
    
    for (const breached of breachedInCell4) {
      // Try to find another cell without a breached anomaly
      const availableCells: LocationId[] = ['cell_1', 'cell_2', 'cell_3'];
      const occupiedCells = new Set(
        breachedAnomalies
          .filter(b => b !== breached)
          .map(b => b.breachCellId)
      );
      
      const freeCell = availableCells.find(cell => !occupiedCells.has(cell));
      
      if (freeCell) {
        // Move to free cell
        breached.breachCellId = freeCell;
      } else {
        // No free cell - trigger escape
        this.gameState.removeBreachedAnomaly(breached);
        const cost = this.gameData.costs.breach_containment_cost;
        this.gameState.addBudget(-cost);
        this.gameState.queueBreachEscape({
          anomalyName: breached.originalAnomaly.data.name,
          cost
        });
      }
    }
    
    // Handle non-breached anomalies in cell_4
    const anomalies = this.gameState.getAnomalies();
    const anomalyInCell4 = anomalies.find(a => a.cellId === cellId);
    
    if (anomalyInCell4) {
      // Move to a random available cell
      const availableCells: LocationId[] = ['cell_1', 'cell_2', 'cell_3'];
      const occupiedCells = new Set(anomalies.filter(a => a !== anomalyInCell4 && a.cellId).map(a => a.cellId!));
      const freeCells = availableCells.filter(cell => !occupiedCells.has(cell));
      
      if (freeCells.length > 0) {
        const randomCell = freeCells[Math.floor(Math.random() * freeCells.length)];
        anomalyInCell4.cellId = randomCell;
      } else {
        // No free cell - remove anomaly (it's lost in time and space)
        this.gameState.removeAnomaly(anomalyInCell4);
      }
    }
  }

  private applyKillRandomResearcher(): void {
    const researchers = this.gameState.getResearchers();
    
    if (researchers.length === 0) {
      return;
    }

    const randomIndex = Math.floor(Math.random() * researchers.length);
    const victim = researchers[randomIndex];
    
    // Queue pending kill modal
    this.gameState.queueRealityKill({
      researcherName: victim.data.name
    });
  }

  private addPersistentEffect(effect: ActiveRealityEffect): void {
    this.gameState.addActiveRealityEffect(effect);
  }
}
